// --- React ---
import { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

// --- Services ---
import parameterUnitSvc from "../../services/api/parameterUnit";

// --- Models ---
import { ParameterUnitItem, ParameterUnitFilter } from "../../models/types/parameterUnit";
import { DefaultFilterRequest, PaginationMeta } from "../../models/types/common";

// --- Components ---
import ListTable from "../../components/list-table";
import Pagination from "../../components/pagination";
import { notify } from "../../common/notificationService";
import ButtonAction from "../../components/button/buttonAction";
import { useNavigate } from "react-router-dom";
import { UrlCollection } from "../../common/url-collection";
import ModalConfirm from "../../components/modal-comfirm";
import MenuItem from "@mui/material/MenuItem";
import ButtonMore from "../../components/button/buttonMore";

// --- Icons ---
import { Trash2, Pencil, ListMinus } from "lucide-react";

// --- Utils ---
import dayjs from "dayjs";
import { useResponsive } from "../../hooks/mediaQuery";
import ParameterUnitFilterView from "./filter";
import { useTranslation } from "react-i18next";

export default function ParameterUnitPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { t } = useTranslation();
    const { isXs, isLg, isXl } = useResponsive();

    const [data, setData] = useState<ParameterUnitItem[]>([]);
    const [pagination, setPagination] = useState<PaginationMeta>({});
    const [orderBy, setOrderBy] = useState<string>("modifiedDate");
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<ParameterUnitItem | null>(null);

    const getListAll = async (params: DefaultFilterRequest<ParameterUnitFilter>) => {
        setLoading(true);
        try {
            const res = await parameterUnitSvc.getAll(params);
            if (res && res.content) {
                const { items, pageCount, totalItemCount, pageIndex, pageSize } = res.content;
                setData(items);
                setPagination({ pageCount, totalItemCount, pageIndex, pageSize });
            }
        } catch (error) {
            console.error(error);
            setData([]);
            setPagination({});
            notify(t("common.error"), t("parameterUnit.notifications.error"), "danger");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (row: ParameterUnitItem) => {
        try {
            const res = await parameterUnitSvc.deleteParameterUnit(row.id);
            if (res && res.content.status) {
                notify(t("common.success"), t("parameterUnit.notifications.deleteSuccess"), "success");
                fetchData();
            }
        } catch (error) {
            console.error(error);
            notify(t("common.error"), t("parameterUnit.notifications.error"), "danger");
        }
    };

    const fetchData = async () => {
        const queryParams = new URLSearchParams(location.search);
        const params: DefaultFilterRequest<ParameterUnitFilter> = {
            sorting: queryParams.get("sorting"),
            pageIndex: queryParams.get("pageIndex") || "1",
            pageSize: queryParams.get("pageSize") || "20",
            name: queryParams.get("name"),
            code: queryParams.get("code"),
            isDelete: queryParams.get("isDelete") || "false",
        };

        await getListAll(params);
    };
    useEffect(() => {
        fetchData();
    }, [location.search]);

    const handlePageChange = (page: number) => {
        searchParams.set("pageIndex", page.toString());
        setSearchParams(searchParams);
    };

    const handleChangePageSize = (size: number) => {
        searchParams.set("pageSize", size.toString());
        searchParams.set("pageIndex", "1"); // reset về trang đầu tiên
        setSearchParams(searchParams);
    };

    const onRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const sort = isAsc ? "desc" : "asc";
        setOrder(sort);
        setOrderBy(property);

        searchParams.set("sorting", `${property} ${sort}`);
        setSearchParams(searchParams);
    };

    const handleEdit = (row: ParameterUnitItem) => {
        navigate(UrlCollection.EditParameterUnit.replace(":id", row.id.toString()));
    };

    const handleDelete = (row: ParameterUnitItem) => {
        setSelectedRow(row);
        setOpenModalConfirm(true);
    };

    const columns = [
        { header: t("parameterUnit.table.stt"), accessor: "index", sortable: false, className: "w-[50px]" },
        { header: t("parameterUnit.table.name"), accessor: "name", sortable: false },
        { header: t("parameterUnit.table.code"), accessor: "code", sortable: false },
        {
            header: t("parameterUnit.table.modifiedDate"),
            accessor: (row: ParameterUnitItem) => `${dayjs(row.modified_date).format("DD/MM/YYYY")}`,
            sortable: true,
        },
        {
            header: t("parameterUnit.table.actions"),
            accessor: (row: ParameterUnitItem) => {
                if (isXl || isLg) {
                    return (
                        <div className="flex gap-2">

                            <ButtonAction
                                onClick={() => handleEdit(row)}
                                icon={<Pencil size={14} />}
                                color="blue"
                                title={t("parameterUnit.actions.edit")}
                            />

                            <ButtonAction
                                onClick={() => handleDelete(row)}
                                icon={<Trash2 size={14} />}
                                color="red"
                                title={t("parameterUnit.actions.delete")}
                            />
                        </div>
                    )
                }
                return (
                    <ButtonMore
                        onClick={() => handleEdit(row)}
                        icon={<ListMinus size={14} />}
                        title={t("parameterUnit.actions.edit")}
                    >
                        <MenuItem onClick={() => handleEdit(row)}>
                            <Pencil size={14} color="blue" className="mr-2" />
                            <span>{t("parameterUnit.actions.edit")}</span>
                        </MenuItem>
                        <MenuItem onClick={() => handleDelete(row)}>
                            <Trash2 size={14} color="red" className="mr-2" />
                            <span>{t("parameterUnit.actions.delete")}</span>
                        </MenuItem>
                    </ButtonMore>
                )
            },
            sortable: false,
            className: "max-w-[100px]",
        },
    ];

    return (
        <div className="page-wrapper list flex flex-col">
            <div className="page-header">
                <h1 className="text-xl font-semibold">{t("parameterUnit.title")}</h1>
                <button
                    className="btn shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => navigate(UrlCollection.CreateParameterUnit)}
                >
                    {t("parameterUnit.addNew")}
                </button>
            </div>
            <div className={`flex ${isXs ? 'my-1' : 'my-2'}`}>
                <ParameterUnitFilterView />
            </div>
            <ListTable
                columns={columns}
                data={data}
                isLoading={loading}
                sortBy={orderBy}
                order={order}
                handleSort={onRequestSort}
            />
            {pagination.totalItemCount ? (
                <Pagination
                    pageCount={pagination.pageCount}
                    totalItemCount={pagination.totalItemCount}
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handleChangePageSize}
                />
            ) : null}
            {openModalConfirm && (
                <ModalConfirm
                    isOpen={openModalConfirm}
                    onClose={() => setOpenModalConfirm(false)}
                    title={t("common.confirm")}
                    content={`${t("parameterUnit.contentConfirm")}${selectedRow?.name}${t("parameterUnit.contentConfirmEnd")}`}
                    titleComfirm={t("common.confirm")}
                    action={() => {
                        onDelete(selectedRow!);
                        setOpenModalConfirm(false);
                    }}
                />
            )}
        </div>
    );
}
